import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchShrinkRecords,
  fetchShrinkSummary,
  parseShrinkMonth,
  shrinkListQuery,
  SHRINK_PAGE_SIZE,
  type ShrinkListResponse,
  type ShrinkRecord,
  type ShrinkSummaryResponse,
} from '../api/shrink';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant, parseInstant } from '../utils/datetime';

export const SHRINK_REPORT_PATH = '/reports/shrink';

/** Display order for shrink events. The list API already returns newest first. */
export function newestShrinkFirst<T extends { created_at: string | null }>(records: T[]): T[] {
  return [...records].sort((left, right) => {
    const leftMs = parseInstant(left.created_at)?.getTime() ?? 0;
    const rightMs = parseInstant(right.created_at)?.getTime() ?? 0;
    return rightMs - leftMs;
  });
}

function displayCount(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '0';
  return String(value);
}

export const ReportsShrinkPage: React.FC = () => {
  const [monthDraft, setMonthDraft] = useState('');
  const [appliedMonth, setAppliedMonth] = useState('');
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState<ShrinkSummaryResponse | null>(null);
  const [list, setList] = useState<ShrinkListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const loadReport = async (event?: React.FormEvent, nextPage?: number) => {
    if (event) event.preventDefault();
    const monthSource = event ? monthDraft : appliedMonth;
    const requestedPage = event ? 1 : (nextPage ?? page);
    const parsed = parseShrinkMonth(monthSource);
    const skip = Math.max(0, requestedPage - 1) * SHRINK_PAGE_SIZE;
    const query = shrinkListQuery({
      month: parsed?.month ?? null,
      year: parsed?.year ?? null,
      skip,
    });
    const current = ++requestId.current;

    try {
      setLoading(true);
      setError(null);
      const [nextSummary, nextList] = await Promise.all([
        fetchShrinkSummary(),
        fetchShrinkRecords(query),
      ]);
      if (current !== requestId.current) return;
      setAppliedMonth(monthSource);
      setPage(requestedPage);
      setSummary(nextSummary);
      setList(nextList);
    } catch (err: unknown) {
      if (current !== requestId.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load shrink report');
      setSummary(null);
      setList(null);
    } finally {
      if (current === requestId.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void loadReport();
  }, []);

  const monthFilter = parseShrinkMonth(appliedMonth);
  const records = newestShrinkFirst(list?.records ?? []);
  const total = list?.total ?? 0;
  const rangeStart = records.length === 0 ? 0 : (page - 1) * SHRINK_PAGE_SIZE + 1;
  const rangeEnd = monthFilter ? records.length : (page - 1) * SHRINK_PAGE_SIZE + records.length;
  const totalPages = monthFilter ? 1 : Math.max(1, Math.ceil(total / SHRINK_PAGE_SIZE));
  const summaryBody = summary?.summary;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Reports: Shrink</h1>
        <p style={styles.subtitle}>
          Losses, damage, and unfound copies recorded for tax write-off. Totals come from the
          shrink summary. A month loads that month&apos;s export; leave it empty for the newest
          events.
        </p>
      </div>

      <form style={styles.filters} onSubmit={loadReport}>
        <label style={styles.label}>
          Month
          <input
            type="month"
            style={styles.input}
            value={monthDraft}
            onChange={(event) => setMonthDraft(event.target.value)}
          />
        </label>
        <button type="submit" style={styles.primaryButton} disabled={loading}>
          {loading ? 'Loading...' : 'Load Report'}
        </button>
        <Link to="/reports/sales-cogs" style={styles.secondaryLink}>
          Sales / COGS
        </Link>
      </form>

      {error && <div style={styles.error}>{error}</div>}

      {summaryBody && list && (
        <>
          <div style={styles.metricRow}>
            <div style={styles.metricCard}>
              <div style={styles.metricLabel}>Total quantity</div>
              <div style={styles.metricValue}>{displayCount(summaryBody.total_quantity)}</div>
            </div>
            <div style={styles.metricCard}>
              <div style={styles.metricLabel}>Total records</div>
              <div style={styles.metricValue}>{displayCount(summaryBody.total_records)}</div>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <caption style={styles.caption}>All-time totals by reason</caption>
              <thead>
                <tr>
                  <th style={styles.th}>Reason</th>
                  <th style={styles.th}>Records</th>
                  <th style={styles.th}>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {summaryBody.by_reason.length === 0 && (
                  <tr>
                    <td colSpan={3} style={styles.emptyCell}>No shrink recorded.</td>
                  </tr>
                )}
                {summaryBody.by_reason.map((row) => (
                  <tr key={row.reason}>
                    <td style={styles.tdStrong}>{row.reason}</td>
                    <td style={styles.td}>{displayCount(row.count)}</td>
                    <td style={styles.td}>{displayCount(row.total_qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={styles.status} role="status">
            {monthFilter
              ? `${records.length} event(s) for ${appliedMonth}. The summary above stays all-time.`
              : records.length === 0
                ? 'No recent shrink events.'
                : `Showing ${rangeStart}-${rangeEnd} of ${total}, newest first.`}
          </p>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>When</th>
                  <th style={styles.th}>Product</th>
                  <th style={styles.th}>Qty</th>
                  <th style={styles.th}>Reason</th>
                  <th style={styles.th}>Order</th>
                  <th style={styles.th}>Recorded by</th>
                  <th style={styles.th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 && (
                  <tr>
                    <td colSpan={7} style={styles.emptyCell}>No shrink events for this view.</td>
                  </tr>
                )}
                {records.map((record) => (
                  <ShrinkEventRow key={record.id} record={record} />
                ))}
              </tbody>
            </table>
          </div>

          {!monthFilter && totalPages > 1 && (
            <div style={styles.pager}>
              <button
                type="button"
                aria-label="Previous page"
                style={{
                  ...styles.secondaryButton,
                  ...(page <= 1 || loading ? styles.buttonDisabled : {}),
                }}
                disabled={loading || page <= 1}
                onClick={() => void loadReport(undefined, page - 1)}
              >
                Previous
              </button>
              <span style={styles.pagerLabel}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                aria-label="Next page"
                style={{
                  ...styles.secondaryButton,
                  ...(page >= totalPages || loading ? styles.buttonDisabled : {}),
                }}
                disabled={loading || page >= totalPages}
                onClick={() => void loadReport(undefined, page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

function ShrinkEventRow({ record }: { record: ShrinkRecord }) {
  return (
    <tr>
      <td style={styles.td}>{formatInstant(record.created_at)}</td>
      <td style={styles.tdMono}>{record.product_id}</td>
      <td style={styles.td}>{record.quantity}</td>
      <td style={styles.tdStrong}>{record.reason}</td>
      <td style={styles.tdMono}>{record.order_id || '—'}</td>
      <td style={styles.td}>{record.created_by}</td>
      <td style={styles.td}>{record.notes || '—'}</td>
    </tr>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'grid',
    gap: spacing.lg,
  },
  header: {
    display: 'grid',
    gap: spacing.xs,
  },
  title: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    maxWidth: '720px',
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'flex-end',
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
  },
  label: {
    display: 'grid',
    gap: spacing.xs,
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
    minWidth: '220px',
  },
  input: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.md,
    color: colors.text,
  },
  primaryButton: {
    border: `1px solid ${colors.primary}`,
    backgroundColor: colors.primary,
    color: colors.white,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    height: '40px',
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    color: colors.text,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    height: '40px',
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  buttonDisabled: {
    backgroundColor: colors.backgroundAlt,
    color: colors.textSecondary,
    cursor: 'not-allowed',
  },
  secondaryLink: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '40px',
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    color: colors.text,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    textDecoration: 'none',
    fontWeight: typographyFlat.fontWeight.semibold,
    fontSize: typography.small.fontSize,
  },
  error: {
    backgroundColor: colors.errorLight,
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.md,
    color: colors.error,
    padding: spacing.sm,
  },
  metricRow: {
    display: 'grid',
    gap: spacing.md,
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  },
  metricCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
    display: 'grid',
    gap: spacing.xs,
  },
  metricLabel: {
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
  },
  metricValue: {
    color: colors.text,
    fontSize: typographyFlat.fontSize.xl,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  status: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  caption: {
    captionSide: 'top',
    textAlign: 'left',
    padding: `${spacing.sm} ${spacing.md}`,
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  tableWrap: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    overflowX: 'auto',
    backgroundColor: colors.surface,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typographyFlat.fontSize.sm,
  },
  tdStrong: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  tdMono: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typography.caption.fontSize,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
  },
  pagerLabel: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
};
